<template>
  <v-app>
    <v-container fluid class="pa-4">
      <v-row>
        <v-col cols="12">
          <!-- Show message when no ID -->
          <v-card v-if="!hasValidId" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-information-outline</v-icon>
              <div class="text-h6 mb-2">No Employee Salary Detail Selected</div>
              <div class="text-body-2 text-medium-emphasis mb-4">
                Please select an employee salary detail from the list to view details.
              </div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="router.push({ name: 'employee-salary-details-list' })"
              >
                <v-icon start>mdi-format-list-bulleted</v-icon>
                Go to Employee Salary Details List
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Show loading state -->
          <v-card v-else-if="loading" elevation="1">
            <v-card-text class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              />
              <div class="text-body-1 mt-4">Loading employee salary details...</div>
            </v-card-text>
          </v-card>

          <!-- Show error state -->
          <v-card v-else-if="error" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
              <div class="text-h6 mb-2 text-error">Error Loading Details</div>
              <div class="text-body-2 text-medium-emphasis mb-4">{{ error }}</div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="fetchEmployeeSalaryDetails"
                class="mr-2"
              >
                Retry
              </v-btn>
              <v-btn
                color="grey"
                variant="text"
                @click="router.push({ name: 'employee-salary-details-list' })"
              >
                Back to List
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Payslip Design -->
          <v-card v-else-if="employeeSalaryData" elevation="2" class="payslip-card">
            <!-- Header Section -->
            <div class="payslip-header text-center pa-4">
              <div class="text-h4 font-weight-bold text-primary mb-2">
                {{ getCurrentCompany?.name || 'Company Name' }}
              </div>
              <div class="text-h6 text-grey-darken-1">
                Payslip for {{ getMonthName(employeeSalaryData.month) }} {{ employeeSalaryData.year }}
              </div>
            </div>

            <v-divider></v-divider>

            <!-- Employee and Company Information -->
            <v-card-text class="pa-4">
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="mb-2">
                    <span class="text-caption text-medium-emphasis">Company: </span>
                    <span class="text-body-1 font-weight-medium">{{ getCurrentCompany?.name || '—' }}</span>
                  </div>
                  <div class="mb-2">
                    <span class="text-caption text-medium-emphasis">Employee Name: </span>
                    <span class="text-body-1 font-weight-medium">{{ employeeSalaryData.employee?.name || '—' }}</span>
                  </div>
                  <div class="mb-2">
                    <span class="text-caption text-medium-emphasis">Department: </span>
                    <span class="text-body-1">{{ employeeSalaryData.department?.name || '—' }}</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-2">
                    <span class="text-caption text-medium-emphasis">Branch: </span>
                    <span class="text-body-1">{{ getCurrentBranch?.name || '—' }}</span>
                  </div>
                  <div class="mb-2">
                    <span class="text-caption text-medium-emphasis">Employee ID: </span>
                    <span class="text-body-1 font-weight-medium">{{ employeeSalaryData.employee?.code || '—' }}</span>
                  </div>
                  <div class="mb-2">
                    <span class="text-caption text-medium-emphasis">Designation: </span>
                    <span class="text-body-1">{{ employeeSalaryData.designation?.name || '—' }}</span>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <!-- Earnings and Deductions Section -->
              <v-row>
                <!-- Earnings Section -->
                <v-col cols="12" md="6">
                  <div class="text-h6 font-weight-bold text-primary mb-3">Earnings</div>
                  <v-table density="compact" class="elevation-1">
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 70%;">Description</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Basic</td>
                        <td class="text-right">{{ employeeSalaryData.basic || employeeSalaryData.basic_payable  }}</td>
                      </tr>
                      <tr>
                        <td>House Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.home_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Medical Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.medical_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Education Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.education_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Transport Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.transport_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Technical Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.technical_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Conveyance</td>
                        <td class="text-right">{{ employeeSalaryData.conveyance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Food Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.food_allowance || employeeSalaryData.tiffin_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Housing Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.housing_allowance || employeeSalaryData.housing_payable || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Wash Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.wash_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Other Allowance</td>
                        <td class="text-right">{{ employeeSalaryData.other_allowance || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Service Charge</td>
                        <td class="text-right">{{ employeeSalaryData.service_charge || employeeSalaryData.service_charge_payable || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>OT Amount</td>
                        <td class="text-right">{{ employeeSalaryData.total_ot_amount || employeeSalaryData.ot_amount || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Incentive Amount</td>
                        <td class="text-right">{{ employeeSalaryData.incentive_amount || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Adjust Amount</td>
                        <td class="text-right">{{ employeeSalaryData.adjust_amount || '0.00' }}</td>
                      </tr>
                      <tr class="font-weight-bold" style="background-color: #f5f5f5;">
                        <td>Total Earnings</td>
                        <td class="text-right">{{ employeeSalaryData.gross_payable || employeeSalaryData.gross_salary || '0.00' }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-col>

                <!-- Deductions Section -->
                <v-col cols="12" md="6">
                  <div class="text-h6 font-weight-bold text-primary mb-3">Deductions</div>
                  <v-table density="compact" class="elevation-1">
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 70%;">Description</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Income Tax</td>
                        <td class="text-right">{{ employeeSalaryData.income_tax || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Advance Amount</td>
                        <td class="text-right">{{ employeeSalaryData.advance_amount || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Advance Refund</td>
                        <td class="text-right">{{ employeeSalaryData.advance_refund || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Revenue Stamp</td>
                        <td class="text-right">{{ employeeSalaryData.revenue_stamp || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Absent Deduction</td>
                        <td class="text-right">{{ employeeSalaryData.absent_deduction || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Late Deduction</td>
                        <td class="text-right">{{ employeeSalaryData.late_deduction || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Gas Bill Deduction</td>
                        <td class="text-right">{{ employeeSalaryData.gas_bill_deduction || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>Service Charge Deduction</td>
                        <td class="text-right">{{ employeeSalaryData.service_charge_deduction || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>LOP</td>
                        <td class="text-right">{{ employeeSalaryData.lop || (getNumberValue(employeeSalaryData.basic) - getNumberValue(employeeSalaryData.basic_payable)) || '0.00' }}</td>
                      </tr>
                      <tr>
                        <td>&nbsp;</td>
                        <td class="text-right">&nbsp;</td>
                      </tr>
                      <tr>
                        <td>&nbsp;</td>
                        <td class="text-right">&nbsp;</td>
                      </tr>
                      <tr>
                        <td>&nbsp;</td>
                        <td class="text-right">&nbsp;</td>
                      </tr>
                      <tr>
                        <td>&nbsp;</td>
                        <td class="text-right">&nbsp;</td>
                      </tr>
                      <tr>
                        <td>&nbsp;</td>
                        <td class="text-right">&nbsp;</td>
                      </tr>
                      <tr>
                        <td>&nbsp;</td>
                        <td class="text-right">&nbsp;</td>
                      </tr>
                      <tr class="font-weight-bold" style="background-color: #f5f5f5;">
                        <td>Total Deductions</td>
                        <td class="text-right">{{ employeeSalaryData.total_deduction || '0.00' }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-col>
              </v-row>

              <!-- Net Payable Section -->
              <v-row class="mt-2">
                <v-col cols="12">
                  <v-table density="compact" class="elevation-1">
                    <tbody>
                      <tr style="background-color: #c8e6c9;">
                        <td class="text-left font-weight-bold" style="width: 50%; padding: 16px;">
                          Net Payable
                        </td>
                        <td class="text-right font-weight-bold" style="padding: 16px; font-size: 1.1em;">
                          {{ employeeSalaryData.total_net_payable || '0.00' }}
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn color="primary" variant="outlined" prepend-icon="mdi-printer" @click="printPayslip">
                Print
              </v-btn>
              <v-btn color="primary" variant="outlined" @click="router.push({ name: 'employee-salary-details-list' })">
                <v-icon class="me-2">mdi-arrow-left</v-icon>
                Back to List
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from 'vue3-toastify'

const route = useRoute()
const router = useRouter()
const store = useStore()
const loading = ref(false)
const employeeSalaryData = ref(null)
const error = ref(null)

// Computed to check if we have a valid ID
const hasValidId = computed(() => {
  const id = route.params.id
  return id && id !== 'undefined' && id !== null && id !== ''
})

// Get company and branch info from store
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getCurrentBranch = computed(() => {
  return store.getters.getCurrentBranch || {}
})

const formatNumber = (value, fraction = 2) => {
  if (value === undefined || value === null || value === '' || isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const getNumberValue = (value) => {
  if (value === undefined || value === null || value === '' || isNaN(value)) return 0
  return Number(value)
}

const getMonthName = (monthNumber) => {
  if (!monthNumber) return '—'
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return monthNames[monthNumber - 1] || monthNumber
}

const printPayslip = () => {
  if (!employeeSalaryData.value) {
    toast.warning('No data to print')
    return
  }

  const company = getCurrentCompany.value
  const branch = getCurrentBranch.value
  const fileServerUrl = store.getters.getFileServerUrl || ''
  const data = employeeSalaryData.value

  // Build earnings rows
  const earningsRows = [
    { label: 'Basic', value: data.basic || data.basic_payable || '0.00' },
    { label: 'House Allowance', value: data.home_allowance || '0.00' },
    { label: 'Medical Allowance', value: data.medical_allowance || '0.00' },
    { label: 'Education Allowance', value: data.education_allowance || '0.00' },
    { label: 'Transport Allowance', value: data.transport_allowance || '0.00' },
    { label: 'Technical Allowance', value: data.technical_allowance || '0.00' },
    { label: 'Conveyance', value: data.conveyance || '0.00' },
    { label: 'Food Allowance', value: data.food_allowance || data.tiffin_allowance || '0.00' },
    { label: 'Housing Allowance', value: data.housing_allowance || data.housing_payable || '0.00' },
    { label: 'Wash Allowance', value: data.wash_allowance || '0.00' },
    { label: 'Other Allowance', value: data.other_allowance || '0.00' },
    { label: 'Service Charge', value: data.service_charge || data.service_charge_payable || '0.00' },
    { label: 'OT Amount', value: data.total_ot_amount || data.ot_amount || '0.00' },
    { label: 'Incentive Amount', value: data.incentive_amount || '0.00' },
    { label: 'Adjust Amount', value: data.adjust_amount || '0.00' }
  ]

  // Build deductions rows
  const deductionsRows = [
    { label: 'Income Tax', value: data.income_tax || '0.00' },
    { label: 'Advance Amount', value: data.advance_amount || '0.00' },
    { label: 'Advance Refund', value: data.advance_refund || '0.00' },
    { label: 'Revenue Stamp', value: data.revenue_stamp || '0.00' },
    { label: 'Absent Deduction', value: data.absent_deduction || '0.00' },
    { label: 'Late Deduction', value: data.late_deduction || '0.00' },
    { label: 'Gas Bill Deduction', value: data.gas_bill_deduction || '0.00' },
    { label: 'Service Charge Deduction', value: data.service_charge_deduction || '0.00' },
    { label: 'LOP', value: data.lop || (getNumberValue(data.basic) - getNumberValue(data.basic_payable)) || '0.00' }
  ]

  // Add empty rows to deductions to match earnings
  while (deductionsRows.length < earningsRows.length) {
    deductionsRows.push({ label: '', value: '' })
  }

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Payslip - ${data.employee?.name || 'Employee'}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; padding: 10px; font-size: 11px; }
          .payslip-header { text-align: center; padding: 10px 0; border-bottom: 2px solid #1976d2; margin-bottom: 10px; }
          .payslip-header h2 { color: #1976d2; font-weight: bold; margin: 0; font-size: 20px; }
          .payslip-header h4 { color: #666; margin: 5px 0 0 0; font-size: 14px; }
          .info-section { margin-bottom: 10px; }
          .info-row { display: flex; margin-bottom: 3px; font-size: 11px; }
          .info-label { font-weight: 600; width: 120px; }
          .earnings-deductions { display: flex; gap: 10px; margin: 10px 0; }
          .earnings-section, .deductions-section { flex: 1; }
          .section-title { font-size: 14px; font-weight: bold; color: #1976d2; margin-bottom: 5px; }
          .payslip-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10px; }
          .payslip-table th, .payslip-table td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
          .payslip-table th { background-color: #f5f5f5; font-weight: bold; font-size: 10px; }
          .payslip-table td.text-right { text-align: right; }
          .payslip-table tr.total-row { background-color: #f5f5f5 !important; font-weight: bold; }
          .payslip-table tr.total-row td { background-color: #f5f5f5 !important; font-weight: bold; border-top: 2px solid #000; }
          .net-payable { margin-top: 10px; }
          .net-payable-table { width: 100%; border-collapse: collapse; }
          .net-payable-table td { border: 1px solid #ddd; padding: 8px; font-size: 14px; }
          .net-payable-table tr { background-color: #c8e6c9 !important; font-weight: bold; }
          .net-payable-table td { background-color: #c8e6c9 !important; }
          .net-payable-table td:first-child { width: 50%; }
          .net-payable-table td:last-child { text-align: right; }
          @page { margin: 0.3in; size: letter; }
          @media print {
            body { padding: 0; font-size: 10px; }
            .no-print { display: none; }
            .payslip-header { padding: 8px 0; margin-bottom: 8px; }
            .payslip-header h2 { font-size: 18px; }
            .payslip-header h4 { font-size: 12px; }
            .info-section { margin-bottom: 8px; }
            .earnings-deductions { margin: 8px 0; gap: 8px; }
            .payslip-table { font-size: 9px; }
            .payslip-table th, .payslip-table td { padding: 3px 5px; }
            .net-payable { margin-top: 8px; }
            .net-payable-table td { padding: 6px; font-size: 12px; }
          }
        </style>
      </head>
      <body>
        <div class="container-fluid">
          <div class="payslip-header">
            <h2>${company?.name || 'Company Name'}</h2>
            <h4>Payslip for ${getMonthName(data.month)} ${data.year}</h4>
          </div>

          <div class="info-section">
            <div class="row">
              <div class="col-md-6">
                <div class="info-row">
                  <span class="info-label">Company:</span>
                  <span>${company?.name || '—'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Employee Name:</span>
                  <span>${data.employee?.name || '—'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Department:</span>
                  <span>${data.department?.name || '—'}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-row">
                  <span class="info-label">Branch:</span>
                  <span>${branch?.name || '—'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Employee ID:</span>
                  <span>${data.employee?.code || '—'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Designation:</span>
                  <span>${data.designation?.name || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="earnings-deductions">
            <div class="earnings-section">
              <div class="section-title">Earnings</div>
              <table class="payslip-table">
                <thead>
                  <tr>
                    <th style="width: 70%;">Description</th>
                    <th style="text-align: right;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${earningsRows.map(row => `
                    <tr>
                      <td>${row.label}</td>
                      <td class="text-right">${row.value}</td>
                    </tr>
                  `).join('')}
                  <tr class="total-row">
                    <td>Total Earnings</td>
                    <td class="text-right">${data.gross_payable || data.gross_salary || '0.00'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="deductions-section">
              <div class="section-title">Deductions</div>
              <table class="payslip-table">
                <thead>
                  <tr>
                    <th style="width: 70%;">Description</th>
                    <th style="text-align: right;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${deductionsRows.map(row => `
                    <tr>
                      <td>${row.label || '&nbsp;'}</td>
                      <td class="text-right">${row.value || '&nbsp;'}</td>
                    </tr>
                  `).join('')}
                  <tr class="total-row">
                    <td>Total Deductions</td>
                    <td class="text-right">${data.total_deduction || '0.00'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="net-payable">
            <table class="net-payable-table">
              <tbody>
                <tr>
                  <td>Net Payable</td>
                  <td class="text-right">${data.total_net_payable || '0.00'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <script>
          window.onload = function() {
            window.print();
          };
        <\/script>
      </body>
    </html>
  `)
  printWindow.document.close()
}

const fetchEmployeeSalaryDetails = async () => {
  const id = route.params.id
  if (!id || id === 'undefined' || id === null || id === '') {
    error.value = 'Employee Salary Detail ID is required'
    return
  }

  // Convert ID to number if it's a string
  const detailId = typeof id === 'string' ? parseInt(id, 10) : id
  if (isNaN(detailId)) {
    error.value = 'Invalid Employee Salary Detail ID'
    return
  }

  loading.value = true
  error.value = null
  try {
    console.log('Fetching employee salary details for ID:', detailId)
    const response = await store.dispatch('getEmployeeSalaryDetails', detailId)
    console.log('Employee salary details response:', response)
    if (response.success) {
      employeeSalaryData.value = response.data
      console.log('Employee salary data loaded:', employeeSalaryData.value)
    } else {
      error.value = response.message || 'Failed to load employee salary details'
      toast.error(error.value)
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Failed to load employee salary details'
    console.error('Error loading employee salary details:', err)
    toast.error(error.value)
  } finally {
    loading.value = false
  }
}

// Watch for route parameter changes
watch(() => route.params.id, (newId) => {
  if (newId && newId !== 'undefined') {
    fetchEmployeeSalaryDetails()
  } else {
    employeeSalaryData.value = null
    error.value = null
  }
}, { immediate: true })
</script>

<style scoped>
.payslip-card {
  max-width: 1000px;
  margin: 0 auto;
}

.payslip-header {
  background-color: #f5f5f5;
}

.v-table {
  border: 1px solid #e0e0e0;
}

.v-table th {
  background-color: #f5f5f5;
  font-weight: 600;
  border-bottom: 2px solid #e0e0e0;
}

.v-table td {
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 12px;
}

.v-table tbody tr:last-child td {
  border-bottom: none;
}
</style>
